import { environment } from 'src/environments/environment';
import { Warehouse } from 'src/app/shared/models/entities';
import { AllocationStrategy } from 'src/app/features/warehouse/models/entities';

/** Sentinel id standing for "let the backend allocate". Real warehouse ids start at 1, and 0 (not
 *  -1) is used because the set-insert row control is `<input type="number" [min]="0" required>` —
 *  a negative value would trip Angular's MinValidator and block the save button, while 0 satisfies
 *  both `min` and `required`. */
export const AUTO_WAREHOUSE_ID = 0;

/** The one strategy the frontend ever sends. FAIR fills the emptiest warehouse first, warehouse
 *  priority as tiebreak. Not user-selectable by design. */
export const AUTO_STRATEGY: AllocationStrategy = 'FAIR';

/** Deliberately nameless: the UI renders the translated label, so no untranslated string is ever
 *  persisted into a draft.
 *
 *  FROZEN on purpose: this single object is assigned into `warehouseRow` of every Auto row and
 *  into the apply-to-all `entityAll`, so it is shared by reference across the whole grid. A stray
 *  `warehouseRow.name = …` anywhere would otherwise mutate the constant itself and every row at
 *  once; freezing turns that into a silent no-op (a throw in strict mode) instead of corruption. */
export const AUTO_WAREHOUSE = Object.freeze({ id: AUTO_WAREHOUSE_ID }) as Warehouse;

/** One created movement as the backend returns it (`Collection<WhMovement>`). */
export interface AllocatedMovement {
  warehouse?: { id?: number | string; name?: string };
  quantity?: number;
}

/** True when a selected warehouse row is the auto sentinel. Accepts the string form because the
 *  `<select>`/`<input>` controls hand back `'0'`. An empty/whitespace-only string id means
 *  "nothing chosen yet" (e.g. an untouched `<select>`'s `<option selected></option>`) — reject it
 *  before the `Number()` coercion, since `Number('')` and `Number(' ')` are both `0` and would
 *  otherwise be misread as an explicit Auto pick. */
export function isAutoWarehouse(row: { id?: number | string } | null | undefined): boolean {
  if (!row || row.id == undefined) {
    return false;
  }
  if (typeof row.id === 'string' && row.id.trim() === '') {
    return false;
  }
  return Number(row.id) === AUTO_WAREHOUSE_ID;
}

/** Role gate for the forced phase. Verbatim match, no hierarchy: `admin` does not imply `user`. */
export function isAutoForced(roles: string[] | null | undefined): boolean {
  const forced = environment.autoAllocationForcedRoles ?? [];
  if (forced.length === 0 || !roles || roles.length === 0) {
    return false;
  }
  return roles.some(role => forced.includes(role));
}

/** "Mag A ×3, Mag B ×2" — what the allocator actually did, for the success toast.
 *
 *  Grouped by warehouse ID, not by name: warehouse names are not unique, so two distinct
 *  warehouses sharing one would otherwise be folded into a single summed entry that overstates
 *  both. The name is only what gets PRINTED for the group (first one seen wins). A movement with
 *  no id at all still groups by name — better a possible fold than dropping the line entirely. */
export function describeAllocationSplit(movements: AllocatedMovement[] | null | undefined): string {
  if (!movements || movements.length === 0) {
    return '';
  }
  const byWarehouse = new Map<string, { name: string; quantity: number }>();
  movements.forEach(movement => {
    const name = movement?.warehouse?.name;
    const id = movement?.warehouse?.id;
    const quantity = Number(movement?.quantity ?? 0);
    if (!name || quantity <= 0) {
      return;
    }
    const key = id != undefined ? `id:${id}` : `name:${name}`;
    const entry = byWarehouse.get(key);
    if (entry) {
      entry.quantity += quantity;
    } else {
      byWarehouse.set(key, { name, quantity });
    }
  });
  return Array.from(byWarehouse.values()).map(entry => `${entry.name} ×${entry.quantity}`).join(', ');
}

/** Dev-only tripwire for the one assumption the whole sentinel design rests on: that no REAL
 *  warehouse ever carries id 0. If the backend ever issues one, that warehouse silently becomes
 *  unpickable (every control would read it as Auto) and an Auto pick would look like a real one —
 *  a failure with no visible symptom at the point it breaks. Warn once, loudly, in dev; a no-op in
 *  production, where a console message helps nobody. */
let sentinelCollisionWarned = false;

export function warnOnSentinelWarehouseCollision(warehouses: Warehouse[] | null | undefined): void {
  if (environment.production || sentinelCollisionWarned || !warehouses?.length) {
    return;
  }
  const collision = warehouses.find(warehouse => warehouse && Number(warehouse.id) === AUTO_WAREHOUSE_ID);
  if (!collision) {
    return;
  }
  sentinelCollisionWarned = true;
  console.warn(
    `[auto-allocation] Warehouse id ${AUTO_WAREHOUSE_ID} is reserved as the "Auto" sentinel, but the ` +
    `backend returned a real warehouse with it ("${collision.name ?? ''}"). That warehouse cannot be ` +
    `selected and Auto picks are ambiguous — the sentinel id must be changed.`
  );
}

/** Test seam: the warn-once latch is module state, so a spec asserting on it needs a way back to
 *  the initial condition. Not called by application code. */
export function resetSentinelCollisionWarning(): void {
  sentinelCollisionWarned = false;
}
